import classNames from 'classnames'
import * as React from 'react'

import {useIsSakuraSelected} from '../../../../hooks/useIsSakuraSelected'
import {getOverviewHref} from '../../../../routes'
import {restApi} from '../../../../services/rest'
import {stringifyId} from '../../../../types'
import type {BuildTypeId, ProjectId} from '../../../../types'
import {base_uri} from '../../../../types/BS_types'
import fileSize from '../../../../utils/filesize'
import {isValue} from '../../../../utils/guards'
import Link from '../../../common/Link/Link'
import ProjectOrBuildTypeIcon from '../../../common/ProjectOrBuildTypeIcon/ProjectOrBuildTypeIcon'
import {EntityIconType} from '../../../common/ProjectOrBuildTypeIcon/ProjectOrBuildTypeIcon.types'
import {ProjectIdContext} from '../CleanupProjectPage.contexts'
import {getPolicyHolderIds} from '../CleanupProjectPage.utils'

import type {Props} from './HolderItem.types'
import {
  getHolderBuildTypeLink,
  getHolderProjectLink,
  getHolderTemplateLink,
  onMoveOutside,
} from './HolderItem.utils'
import TemplateSection from './TemplateSection/TemplateSection'

import styles from './HolderItem.css'

const getTemplateUsagesLink = (
  projectId: ProjectId | undefined,
  templateId: BuildTypeId | ProjectId,
) =>
  projectId != null
    ? `${base_uri}/admin/editProject.html?tab=usagesReport&projectId=${stringifyId(
        projectId,
      )}&templateId=${stringifyId(templateId)}`
    : null

const MB = 1000000

function HolderItem({
  className,
  internalId,
  externalId,
  name,
  buildTypeTemplates,
  type,
  owner,
  isComposite,
}: Props) {
  const pageProjectId = React.useContext(ProjectIdContext)
  const {diskUsage} = restApi.endpoints.getDiskUsage.useQuery(pageProjectId, {
    selectFromResult: ({data}) => ({
      diskUsage: data?.[type === 'project' ? 'project' : 'buildType'][stringifyId(externalId)] ?? 0,
    }),
  })
  const {projectId, buildTypeId} = getPolicyHolderIds(
    {
      externalId,
    },
    type,
  )
  const isSakuraSelected = useIsSakuraSelected()
  let href
  if (type === 'template') {
    href = getHolderTemplateLink(buildTypeId)
  } else {
    href = isSakuraSelected
      ? getOverviewHref({
          projectId,
          buildTypeId,
        })
      : getHolderProjectLink(projectId) || getHolderBuildTypeLink(buildTypeId)
  }

  const templates = buildTypeTemplates?.templates
  const templatesContent = React.useMemo(
    () => (templates != null ? <TemplateSection templates={templates} /> : null),
    [templates],
  )
  const templateUsagesLink =
    type === 'template' ? getTemplateUsagesLink(owner.externalId, externalId) : null
  return (
    <div className={classNames(styles.item, className)}>
      {href != null && isValue(EntityIconType, type) && (
        <Link href={href} className={styles.name}>
          {WrapText => (
            <>
              <ProjectOrBuildTypeIcon
                className={styles.icon}
                type={type}
                size="S"
                composite={isComposite}
              />
              <WrapText>{name}</WrapText>
            </>
          )}
        </Link>
      )}
      {templateUsagesLink != null && (
        <Link
          className={styles.usages}
          key={stringifyId(internalId)}
          onClick={onMoveOutside}
          href={templateUsagesLink}
        >
          {'Usages'}
        </Link>
      )}
      {templatesContent}
      {type !== 'template' && (
        <p className={styles.diskUsage}>
          {`Disk usage: ${diskUsage > MB ? fileSize(diskUsage) : '<1 MB'}`}
        </p>
      )}
    </div>
  )
}

export default HolderItem
